/**
 * Lists all products that can be added to the cart and checked out.
 *
 * !!! DO NOT EDIT id values once they go live.
 *
 * !!! Only edit product costs if all orders in Stripe have been recorded in Contentful.
 *     Otherwise, we may create the order with incorrect individual bundle/product costs,
 *     which will show up incorrectly in the user-facing account receipts.
 */

import { Discount, LICENSE_BUNDLE_DISCOUNT, findDiscount } from "./Discounts";

export const DEFAULT_FONT_PRICE = 8000; // 80 dollars

interface BaseProduct {
  id: number;
  name: string;
  cssName: string;

  getBasePrice: (numLicenses: number, isEducationalLicense: boolean) => number;
  getDiscount: (allProductsInCart: Product[]) => Discount | null;
}

export class FontProduct implements BaseProduct {
  id: number;
  name: string;
  cssName: string;
  filename: string;
  filepath: string;

  constructor(vals: {
    id: number;
    name: string;
    cssName: string;
    filename: string;
    filepath: string;
  }) {
    this.id = vals.id;
    this.name = vals.name;
    this.cssName = vals.cssName;
    this.filename = vals.filename;
    this.filepath = vals.filepath;
    Object.freeze(this);
  }

  getBasePrice(numLicenses: number, isEducationalLicense: boolean): number {
    const licenseMult =
      1 + (1 - LICENSE_BUNDLE_DISCOUNT) * Math.max(0, numLicenses - 1); // TODO: Calculate this in the shopping cart details
    const educationalMult = isEducationalLicense ? 0.5 : 1;
    return licenseMult * educationalMult * DEFAULT_FONT_PRICE;
  }

  getDiscount(allProductsInCart: Product[]) {
    return findDiscount(this, allProductsInCart);
  }
}

export class BundleProduct implements BaseProduct {
  id: number;
  name: string;
  cssName: string;
  bundleIncludes: number[];
  complete?: boolean;

  constructor(vals: {
    id: number;
    name: string;
    cssName: string;
    bundleIncludes: number[];
    complete?: boolean;
  }) {
    this.id = vals.id;
    this.name = vals.name;
    this.cssName = vals.cssName;
    this.bundleIncludes = vals.bundleIncludes;
    this.complete = vals.complete;
    Object.freeze(this);
  }

  getBasePrice(numLicenses: number, isEducationalLicense: boolean): number {
    return PRODUCTS.filter((p) => this.bundleIncludes.includes(p.id)).reduce(
      (accum, currentProduct) =>
        accum + currentProduct.getBasePrice(numLicenses, isEducationalLicense),
      0
    );
  }

  getDiscount(allProductsInCart: Product[]) {
    return findDiscount(this, allProductsInCart);
  }
}

export type Product = FontProduct | BundleProduct;

const felicetteStyle = (name) => {
  return {
    name: `Felicette ${name}`,
    cssName: `ST Felicette ${name}`,
    filename: `Felicettev0.2-${name}.otf`,
    filepath: `https://fonts-priv.vercel.app/rj03j0fg2gh32gh20r012rj10fg/Felicettev0.2-${name}.otf`,
  };
};

const felicetteScriptStyle = (name) => {
  return {
    name: `Felicette Script ${name}`,
    cssName: `ST Felicette Script ${name}`,
    filename: `STFelicetteScript-${name}.ttf`,
    filepath: `https://fonts-priv.vercel.app/rj03j0fg2gh32gh20r012rj10fg/STFelicetteScript-${name}.ttf`,
  };
};

export const FELICETTE_PRODUCTS: Product[] = [
  { id: 0, ...felicetteStyle("Thin") },
  { id: 1, ...felicetteStyle("Light") },
  { id: 2, ...felicetteStyle("Regular") },
  { id: 3, ...felicetteStyle("Medium") },
  { id: 4, ...felicetteStyle("Bold") },
  { id: 5, ...felicetteStyle("ExtraBold") },

  { id: 6, ...felicetteScriptStyle("Thin") },
  { id: 7, ...felicetteScriptStyle("Light") },
  { id: 8, ...felicetteScriptStyle("Regular") },
  { id: 9, ...felicetteScriptStyle("Medium") },
  { id: 10, ...felicetteScriptStyle("Bold") },
  { id: 11, ...felicetteScriptStyle("ExtraBold") },
  {
    id: 12,
    name: "Complete Felicette & Script Family",
    cssName: `ST Felicette Bold`,
    bundleIncludes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    complete: true,
  },
  {
    id: 13,
    name: "Felicette Sans Subfamily",
    cssName: `ST Felicette Medium`,
    bundleIncludes: [0, 1, 2, 3, 4, 5],
  },
  {
    id: 14,
    name: "Felicette Script Subfamily",
    cssName: `ST Felicette Script Medium`,
    bundleIncludes: [6, 7, 8, 9, 10, 11],
  },
].map((obj) =>
  obj.bundleIncludes ? new BundleProduct(obj) : new FontProduct(obj)
);

const monochromicStyle = (name) => {
  return {
    name: `Monochromic ${name}`,
    cssName: `ST Monochromic ${name}`,
    filename: `STMonochromicv.01-${name}.otf`,
    filepath: `https://fonts-priv.vercel.app/iaohe8rq2rh2981rt901thf8g032038gh/STMonochromicv.01-${name}.otf`,
  };
};

// todo
const monochromicScriptStyle = (name) => {
  return {
    name: `Monochromic Script ${name}`,
    cssName: `ST Monochromic ${name}`,
    filename: `STMonochromicv.01-${name}.otf`,
    filepath: `https://fonts-priv.vercel.app/iaohe8rq2rh2981rt901thf8g032038gh/STMonochromicv.01-${name}.otf`,
  };
};

export const MONOCHROMIC_PRODUCTS = [
  { id: 15, ...monochromicStyle("Thin") },
  { id: 16, ...monochromicStyle("ExtraLight") },
  { id: 17, ...monochromicStyle("Light") },
  { id: 18, ...monochromicStyle("Regular") },
  { id: 19, ...monochromicStyle("Medium") },
  { id: 20, ...monochromicStyle("SemiBold") },
  { id: 21, ...monochromicStyle("Bold") },
  { id: 22, ...monochromicScriptStyle("Thin") },
  { id: 23, ...monochromicScriptStyle("ExtraLight") },
  { id: 24, ...monochromicScriptStyle("Light") },
  { id: 25, ...monochromicScriptStyle("Regular") },
  { id: 26, ...monochromicScriptStyle("Medium") },
  { id: 27, ...monochromicScriptStyle("SemiBold") },
  { id: 28, ...monochromicScriptStyle("Bold") },
  {
    id: 29,
    name: "Complete Monochromic & Script Family",
    cssName: `ST Monochromic Medium`,
    bundleIncludes: [15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28],
  },
  {
    id: 30,
    name: "Monochromic Sans Subfamily",
    cssName: `ST Monochromic Medium`,
    bundleIncludes: [15, 16, 17, 18, 19, 20, 21],
  },
  {
    id: 31,
    name: "Monochromic Script Subfamily",
    cssName: `ST Monochromic Medium`,
    bundleIncludes: [22, 23, 24, 25, 26, 27, 28],
  },
].map((obj) =>
  obj.bundleIncludes ? new BundleProduct(obj) : new FontProduct(obj)
);

const ampersandistStyle = (name) => {
  return {
    name: `Ampersandist ${name}`,
    cssName: `ST Ampersandist ${name}`,
    filename: `STAmpersandist-${name}.ttf`,
    filepath: `https://fonts-priv.vercel.app/29ru1092uf910fgj290jg90/STAmpersandist-${name}.ttf`,
  };
};

export const AMPERSANDIST_PRODUCTS = [
  { id: 32, ...ampersandistStyle("Regular") },
  { id: 33, ...ampersandistStyle("Medium") },
  { id: 34, ...ampersandistStyle("SemiBold") },
  { id: 35, ...ampersandistStyle("Bold") },
  { id: 36, ...ampersandistStyle("ExtraBold") },
  { id: 37, ...ampersandistStyle("Black") },
  { id: 38, ...ampersandistStyle("ExtraBlack") },
  {
    id: 39,
    name: "Complete Ampersandist Family",
    cssName: `ST Ampersandist SemiBold`,
    bundleIncludes: [32, 33, 34, 35, 36, 37, 38],
  },
].map((obj) =>
  obj.bundleIncludes ? new BundleProduct(obj) : new FontProduct(obj)
);

export const NEBULITH_PRODUCTS = [
  {
    id: 40,
    name: "Nebulith Display",
    cssName: "ST Nebulith Display",
    filename: "STNebulithDisplay-Regular.ttf",
    filepath:
      "https://fonts-priv.vercel.app/jfhuhg3ohofhf2oh2ofhacafa2fafv/STNebulithDisplay-Regular.ttf",
  },
  {
    id: 41,
    name: "Nebulith Ultra Display",
    cssName: "ST Nebulith Ultra Display",
    filename: "SolsticeUltraDisplayv0.05-Regular.otf",
    filepath:
      "https://fonts-priv.vercel.app/jfhuhg3ohofhf2oh2ofhacafa2fafv/SolsticeUltraDisplayv0.05-Regular.otf",
  },
  {
    id: 42,
    name: "Complete Nebulith Family",
    cssName: `ST Nebulith Display`,
    bundleIncludes: [40, 41],
  },
].map((obj) =>
  obj.bundleIncludes ? new BundleProduct(obj) : new FontProduct(obj)
);

export const PRODUCTS: Product[] = [
  ...FELICETTE_PRODUCTS,
  ...MONOCHROMIC_PRODUCTS,
  ...AMPERSANDIST_PRODUCTS,
  ...NEBULITH_PRODUCTS,
];

export const PRODUCTS_MAP: { [id: number]: Product } = Object.fromEntries(
  PRODUCTS.map((product) => [product.id, product])
);

export function isBundleProduct(product: Product): boolean {
  return "bundleIncludes" in product;
}

export function isCompleteBundle(product: Product): boolean {
  return "complete" in product && product.complete == true;
}
